import { projector, renderCurves } from './research-wireframes.js';

// Transcribed from the task images in the ARC article. Symbols stand in for
// colors; reveal order is illustrative, not a recorded inference trace.
export const arcExamples = [
    {id:'05269061', input:['0000000','0000000','0000001','0000012','0000124','0001240','0012400'], output:['2412412','4124124','1241241','2412412','4124124','1241241','2412412']},
    {id:'00d62c1b', input:['0000000000','0033330000','0030030000','0030030300','0033333330','0003000030','0003000330','0003300303','0003030030','0000300000'], output:['0000000000','0033330000','0034430000','0034430300','0033333330','0003000030','0003000330','0003300343','0003430030','0000300000']},
];
const clamp = t => Math.max(0,Math.min(1,t));
const ease = t => {t=clamp(t);return t*t*(3-2*t);};
const line = points => ({points});
const rect = (x,y,w,h,z=0) => line([[x,y,z],[x+w,y,z],[x+w,y+h,z],[x,y+h,z],[x,y,z]]);
const glyph = value => {
    if(value==='1') return [line([[-.3,.3,0],[.3,-.3,0]])];
    if(value==='2') return [line([[-.3,-.3,0],[.3,.3,0]])];
    if(value==='3') return [rect(-.3,-.3,.6,.6)];
    if(value==='4') return [line([[-.3,-.3,0],[.3,.3,0]]),line([[-.3,.3,0],[.3,-.3,0]])];
    if(value==='?') return [rect(-.06,-.06,.12,.12)];
    return [];
};
const move = (curves,x,y,size,z=0) => curves.map(({points})=>line(points.map(p=>[x+p[0]*size,y+p[1]*size,z+p[2]])));
const cell = value => [rect(-.46,-.46,.92,.92),...glyph(value)];
const separator = [line([[-.25,-.25,0],[.25,-.25,0],[.25,.25,0],[-.25,.25,0]]),line([[-.25,.25,0],[-.07,.08,0]])];

export function arcRevealOrder(example) {
    // Rank the serialized output, including one row separator per row. Copied
    // cells and structural tokens resolve before harder pattern completion.
    // The ordering is illustrative; every batch draws from this same token pool.
    const n=example.input.length,tokens=[];
    for(let r=0;r<n;r++) for(let c=0;c<=n;c++) {
        const index=r*(n+1)+c;
        const easy=c===n || example.input[r][c]===example.output[r][c];
        tokens.push({index,priority:(easy?0:1000)+(index*37)%101});
    }
    return tokens.sort((a,b)=>a.priority-b.priority).map(({index})=>index);
}
export function arcDiffusionState(seconds,reduced=false) {
    const cycle=Math.floor(Math.max(0,seconds)/16),phase=reduced?14:Math.max(0,seconds)%16;
    const example=reduced?0:cycle%arcExamples.length,n=arcExamples[example].input.length;
    // n equal batches of n+1 tokens exactly exhaust cells plus separators.
    const batchSize=n+1,batchDuration=10/n;
    const batch=Math.min(n,Math.max(0,Math.floor((phase-1)/batchDuration)+1));
    return {example,phase,batchSize,batchDuration,batch,revealed:batch*batchSize,structure:ease(batch/n),solved:batch/n};
}
export function arcTokenRevealTime(rank,n) {
    return 1+Math.floor(rank/(n+1))*(10/n);
}
// A committed cell springs out of the plane, then settles without changing its
// token. This is display depth, not another sampling/remasking step.
export function arcCellDepth(age, reduced=false) {
    if(reduced || age<0 || age>=1.5)return 0;
    return -110*Math.exp(-3.6*age)*Math.cos(9*age)*(1-ease((age-1)/.5));
}
function model(example) {
    const n=example.input.length,step=180/n,curves=[],ranks=[];
    arcRevealOrder(example).forEach((index,rank)=>ranks[index]=rank);
    for(let r=0;r<n;r++) for(let c=0;c<n;c++) {
        curves.push(...move(cell(example.input[r][c]),-220+(c+.5)*step,-95+(r+.5)*step,step));
    }
    // Keep the complete serialized input beneath the two panels, including its
    // row separators. This strip is never masked or removed during a solve.
    const tokenStep=440/(n*(n+1));
    for(let r=0;r<n;r++) for(let c=0;c<=n;c++) {
        const x=-220+(r*(n+1)+c+.5)*tokenStep;
        curves.push(...move(c===n?separator:cell(example.input[r][c]),x,119,tokenStep*.9));
    }
    curves.push(line([[-220,108,0],[-220,100,0],[-40,100,0]]));
    curves.push(line([[-20,-5,0],[10,-5,0],[2,-11,0]]),line([[10,-5,0],[2,1,0]]));
    const cells=example.output.join('').split('').map(value=>({masked:cell('?'),solved:cell(value)}));
    return {n,step,curves,ranks,cells};
}

// The separator fold is a display of the 1D encoding, not a claim that the
// model reshapes its hidden states into a grid. All layouts share one decoder.
export function createArcDiffusionScene(variant='fold') {
    const models=arcExamples.map(model);
    let lastKey,last;
    return {labels:[],draw(seconds,small,reduced=false){
        const t=reduced?0:Math.floor(seconds*(small?24:30))/(small?24:30);
        const key=`${t}/${small}/${reduced}`;
        if(key===lastKey)return last;
        const state=arcDiffusionState(t,reduced),m=models[state.example];
        const project=projector({time:t*.12,phase:2.5,x:small?380:315,y:small?188:237,scale:small?1.12:1.05,depth:.7});
        let path=renderCurves(m.curves,project,0,true);
        for(let r=0;r<m.n;r++) {
            const separatorToken=r*(m.n+1)+m.n;
            const separatorReady=m.ranks[separatorToken]<state.revealed;
            const separatorAge=state.phase-arcTokenRevealTime(m.ranks[separatorToken],m.n);
            const rowProgress=reduced||variant==='grid'?1:variant==='ribbon'?state.structure:
                separatorReady?ease(separatorAge/.65):0;
            for(let c=0;c<m.n;c++) {
                const index=r*m.n+c,token=r*(m.n+1)+c;
                const sx=30+(token%28)*6.7,sy=-120+Math.floor(token/28)*12;
                const tx=30+(c+.5)*m.step,ty=-95+(r+.5)*m.step;
                const x=sx+(tx-sx)*rowProgress,y=sy+(ty-sy)*rowProgress;
                const size=6+(m.step-6)*rowProgress;
                const ready=m.ranks[token]<state.revealed;
                const age=state.phase-arcTokenRevealTime(m.ranks[token],m.n);
                const z=Math.sin(rowProgress*Math.PI)*(variant==='ribbon'?85:55)+(ready?arcCellDepth(age,reduced):0);
                if(ready && Math.abs(z)>2) {
                    // Short corner stalks make the front/back displacement read
                    // as depth rather than a sideways move in the flat grid.
                    for(const [cx,cy] of [[-.46,-.46],[.46,.46]]) {
                        const a=project([x+cx*size,y+cy*size,0]),b=project([x+cx*size,y+cy*size,z]);
                        path+=`M${a[0].toFixed(1)},${a[1].toFixed(1)}L${b[0].toFixed(1)},${b[1].toFixed(1)}`;
                    }
                }
                path+=renderCurves(ready?m.cells[index].solved:m.cells[index].masked,p=>project([x+p[0]*size,y+p[1]*size,z]),0,true);
            }
            // The separator occupies a masked slot and consumes one reveal in
            // its batch, just like a cell. Its reveal triggers this row's fold.
            const sx=30+(separatorToken%28)*6.7,sy=-120+Math.floor(separatorToken/28)*12;
            const x=sx+(218-sx)*rowProgress,y=sy+(-95+(r+.5)*m.step-sy)*rowProgress;
            const size=6+2*rowProgress;
            const z=separatorReady?arcCellDepth(separatorAge,reduced):0;
            path+=renderCurves(separatorReady?separator:cell('?'),p=>project([x+p[0]*size,y+p[1]*size,z]),0,true);
        }
        lastKey=key;last={path,labels:[]};return last;
    }};
}
